import { useState, FormEvent } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { type FirebaseError } from 'firebase/app'
import { sendEmailVerification } from 'firebase/auth'
import { useAuth } from '../hooks/useAuth.js'
import { isOnboarded } from './OnboardingPage.js'
import { cn } from '../lib/cn.js'

const AUTH_ERROR_KEYS: Record<string, string> = {
  'auth/invalid-credential':      'auth.errorInvalidCredentials',
  'auth/invalid-login-credentials': 'auth.errorInvalidCredentials',
  'auth/user-not-found':          'auth.errorInvalidCredentials',
  'auth/wrong-password':          'auth.errorInvalidCredentials',
  'auth/email-already-in-use':    'auth.errorEmailInUse',
  'auth/weak-password':           'auth.errorWeakPassword',
  'auth/too-many-requests':       'auth.errorTooMany',
  'auth/popup-closed-by-user':    'auth.errorPopupClosed',
}

function authErrorKey(err: unknown): string {
  const code = (err as FirebaseError)?.code ?? ''
  return AUTH_ERROR_KEYS[code] ?? 'auth.errorGeneric'
}

export default function AuthPage() {
  const { t }                     = useTranslation()
  const nav                       = useNavigate()
  const { signInGoogle, signInEmail, signUpEmail, signOut } = useAuth()
  const [mode,     setMode]     = useState<'signin' | 'signup'>('signin')
  const [email,    setEmail]    = useState('')
  const [password, setPassword] = useState('')
  const [error,    setError]    = useState('')
  const [busy,     setBusy]     = useState(false)
  const [searchParams] = useSearchParams()
  const verificationSent = searchParams.get('verify') === '1'
  const verifyEmail = searchParams.get('email') ?? ''

  const afterAuth = (uid: string, isNewUser = false) => {
    if (isNewUser || !isOnboarded(uid)) {
      nav('/onboarding')
    } else {
      nav('/dashboard')
    }
  }

  const handleGoogle = async () => {
    setError('')
    setBusy(true)
    try {
      const { user: u } = await signInGoogle()
      afterAuth(u.uid)
    } catch (err) {
      setError(t(authErrorKey(err)))
    } finally {
      setBusy(false)
    }
  }

  const handleEmail = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    setBusy(true)
    try {
      if (mode === 'signin') {
        const { user: u } = await signInEmail(email, password)
        if (!u.emailVerified) {
          await signOut()
          setError(t('auth.errorNotVerified'))
          return
        }
        afterAuth(u.uid, false)
      } else {
        const { user: u } = await signUpEmail(email, password)
        await sendEmailVerification(u)
        await signOut()
        nav(`/auth?verify=1&email=${encodeURIComponent(email)}`, { replace: true })
      }
    } catch (err) {
      setError(t(authErrorKey(err)))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="auth-page">
      {/* Radial glow */}
      <div className="auth-glow" />

      <div className="auth-card">
        {/* Gradient top border */}
        <div className="auth-card-border" />

        {verificationSent ? (
          <>
            <div style={{ textAlign: 'center', fontSize: 36, marginBottom: 16 }}>&#9993;</div>
            <h1 style={{ fontSize: 20, fontWeight: 800, textAlign: 'center', marginBottom: 12 }}>
              {t('auth.verifyTitle')}
            </h1>
            <p style={{ textAlign: 'center', fontSize: 14, color: 'var(--color-text-muted)', lineHeight: 1.5, marginBottom: 24 }}>
              {t('auth.verifyMessage', { email: verifyEmail })}
            </p>
            <button
              onClick={() => nav('/auth', { replace: true })}
              className="auth-submit-btn"
            >
              {t('auth.backToSignIn')}
            </button>
          </>
        ) : (
          <>
            <h1 style={{ fontSize: 22, fontWeight: 800, textAlign: 'center', marginBottom: 28 }}>
              {t(mode === 'signin' ? 'auth.signIn' : 'auth.signUp')}
            </h1>

            <button
              onClick={handleGoogle}
              disabled={busy}
              className="auth-google-btn"
            >
              <GoogleIcon />
              {t('auth.continueGoogle')}
            </button>

            <div style={{ textAlign: 'center', color: 'var(--color-text-muted)', fontSize: 13, margin: '20px 0' }}>
              {t('auth.orEmail')}
            </div>

            <form onSubmit={handleEmail}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 5 }}>{t('auth.email')}</label>
              <input type="email" required value={email} onChange={e => setEmail(e.target.value)} className="input" style={{ marginBottom: 14 }} autoComplete="email" />

              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 5 }}>{t('auth.password')}</label>
              <input type="password" required value={password} onChange={e => setPassword(e.target.value)} className="input" style={{ marginBottom: 14 }} autoComplete={mode === 'signup' ? 'new-password' : 'current-password'} />

              {error && <p style={{ color: 'var(--color-error)', fontSize: 13, marginBottom: 12 }}>{error}</p>}

              <button
                type="submit"
                disabled={busy}
                className={cn('auth-submit-btn', busy && 'opacity-70')}
              >
                {t(mode === 'signin' ? 'auth.signInBtn' : 'auth.signUpBtn')}
              </button>
            </form>

            <p style={{ textAlign: 'center', fontSize: 13, marginTop: 20, color: 'var(--color-text-muted)' }}>
              {t(mode === 'signin' ? 'auth.switchToSignUp' : 'auth.switchToSignIn')}{' '}
              <button
                onClick={() => setMode(m => m === 'signin' ? 'signup' : 'signin')}
                className="auth-mode-toggle"
              >
                {t(mode === 'signin' ? 'auth.signUp' : 'auth.signIn')}
              </button>
            </p>
          </>
        )}
      </div>

      <style>{`
        .auth-page {
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: calc(100vh - 64px);
          padding: 32px 16px;
          position: relative;
        }
        .auth-glow {
          position: absolute;
          top: 30%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 420px;
          height: 420px;
          background: radial-gradient(circle, rgba(255,0,110,0.08) 0%, transparent 70%);
          pointer-events: none;
          z-index: 0;
        }
        .auth-card {
          position: relative;
          z-index: 1;
          background: var(--color-surface);
          border: 1px solid var(--color-glass-border);
          border-radius: var(--radius-lg);
          box-shadow: var(--shadow-lg);
          max-width: 400px;
          width: 100%;
          padding: 40px 32px;
          overflow: hidden;
        }
        .auth-card-border {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 2px;
          background: linear-gradient(90deg, #FF006E, #FFBE0B, #00F5D4);
        }
        .auth-google-btn {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          padding: 11px 0;
          border-radius: 10px;
          border: 1.5px solid var(--color-glass-border);
          background: transparent;
          color: var(--color-text);
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.18s;
          font-family: inherit;
        }
        .auth-google-btn:hover:not(:disabled) {
          background: var(--color-surface-raised);
          transform: translateY(-1px);
        }
        .auth-google-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .auth-submit-btn {
          width: 100%;
          padding: 12px 0;
          border-radius: 10px;
          border: none;
          background: linear-gradient(135deg, #FF006E, #FFBE0B);
          color: #fff;
          font-size: 15px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.18s;
          font-family: inherit;
          margin-top: 4px;
        }
        .auth-submit-btn:hover:not(:disabled) {
          opacity: 0.9;
          transform: translateY(-1px);
        }
        .auth-submit-btn:disabled {
          cursor: not-allowed;
        }
        .auth-mode-toggle {
          background: none;
          border: none;
          color: var(--color-accent);
          cursor: pointer;
          font-weight: 600;
          padding: 0;
          font-size: inherit;
          font-family: inherit;
        }
        .auth-mode-toggle:hover {
          text-decoration: underline;
        }
      `}</style>
    </div>
  )
}

/* ── Google icon ────────────────────────────────────────────────────────────── */

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 48 48">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59A14.5 14.5 0 019.5 24c0-1.59.28-3.14.76-4.59l-7.98-6.19A23.99 23.99 0 000 24c0 3.77.9 7.35 2.56 10.53l7.97-5.94z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 5.94C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  )
}
